<template>
  <form id="kombiAntrag">
    <div class="my-4">
      <div class="bgTRnoGrid px-4 px-lg-5 py-1 py-lg-1">                      
        <div class="row">
          <div class="mt-4 mb-2 col-12 col-md-6 col-lg-3">
                  <SelectField
                  id="versbeginn"
                  label="Versicherungsbeginn *"
                  :options="versbeginnoptions.map(option => ({ value: option, text: option }))"
                  v-model="selectedVersicherungsbeginn"
                  placeholder="Ihre Auswahl"
                  />
          </div>
            <div class="mt-4 mb-2 col-12 col-lg-9 d-flex align-items-center justify-content-center justify-content-md-start">
                  <RdnummerDialog v-if="shouldShowRdnummer" />
                  <RdnummerOk 
                    v-if="shouldShowRdnummerInfo" 
                    :rdnummer="store.aok" 
                    class="mb-3"
                  />
          </div>
          
        </div> <!-- row -->

        <!-- Iteration über alle Personen/Items -->
        <div v-for="item in store.items" :key="item.id" class="boxTR mb-4 p-3">
          <div class="row g-3">
            <div class="col-12">              
                <div class="row">
                  <div class="col-12 col-md-3">
                    <DateTextPickerField
                      :modelValue="item.geburtstag"
                      @update:modelValue="(val) => updateGeburtstag(item.id, val)"
                      :id="`vpGeburtstag${item.id}`"
                      label="Geburtsdatum *"
                      required
                    />
                  </div> <!-- col-12 col-md-3 -->
                </div> <!-- row -->
            </div> <!-- col-12 -->
            
              <KombiButtons :item="item" @tarif-changed="(tarifNummer) => onTarifChanged(item.id, tarifNummer)" />

              <!-- Verstecktes Feld für Tarif-Validierung -->
              <input
                type="checkbox"
                hidden
                class="form-check-input"
                :id="`vpTarif${item.id}`"
                :name="`vpTarif${item.id}`"
                v-model="item.hasTarif"
              >

              <div class="col-12 d-flex flex-column-reverse flex-md-row justify-content-between gap-3">
                <div class="d-flex flex-column align-items-center me-md-3 align-self-center align-self-md-start">
                  <KombiPersonNummer :index="store.items.indexOf(item)" />
                  <div class="mt-2">
                    <PersonEntfernen 
                      v-if="store.items.length >= 2" 
                      @click="personEntfernen(item.id)" 
                    />
                  </div>
                </div>
                
                <KombiPerson :item="item" class="align-self-end align-self-md-center" />
              </div>
            
          </div> <!-- row -->
        </div> <!-- item -->

        <!-- ZX2 Bestätigungs-Checkbox (nur wenn ZX2 gewählt UND Kanal nicht vigoselect) -->
        <div class="row mt-2 mb-3" v-if="store.hatZX2() && mainStore.kanal !== 'vigoselect'">
          <div class="col">
            <div class="d-flex align-items-start gap-2">
              <input
                v-model="store.checkZx2"
                type="checkbox"
                class="form-check-input mt-1 flex-shrink-0"
                id="vpCheckZx2"
                name="vpCheckZx2"
              >
              <label class="form-check-label" for="vpCheckZx2">
                Zum Tarif Zahnersatz x2: Ich bestätige, dass bei allen zu versichernden Personen jeweils maximal zwei Zähne fehlen.
              </label>
              <KombiZX2Popover />
            </div>
          </div>
        </div>

        <WeiterePersonVersichern
          :gesamtbetrag="store.gesamt"
          :showButton="store.items.length < 5"
          @click="personHinzufuegen"
        />

      </div>
    </div>
    
    <!-- Fehleranzeige bei API-Fehler -->
    <FehleranzeigeKlein 
      v-if="antragIdError" 
      :message="antragIdError" 
      class="mb-3"
    />
    
    <div class="d-flex justify-content-end">
      <OnlineAbschliessen @click="handleWeiter" />
    </div>
    
    <!-- Loading-Anzeige -->
    <p v-if="isGeneratingAntragId" class="text-end text-muted mt-2 font-sm-links">
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Antragsnummer wird generiert...
    </p>
    
    <p class="text-end text-muted mt-2 font-sm-links">* Pflichtfeld</p>
  </form>
</template>

<script setup>
import { computed, ref, nextTick, onMounted } from 'vue';
import { formValidation } from "@form-validation/bundle/popular";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import moment from "moment";

import SelectField from "@/fields/SelectField.vue";
import { useRechnerStore } from '@/stores/rechner';
import { useMainStore } from '@/stores/useMainStore.js'
import { useAntragStore } from '@/stores/antrag.js'
import { isValidDate } from '@/helpers/hilfsfunktionen';

import KombiButtons from '@/components/kombi/kombi_buttons.vue';
import KombiPerson from '@/components/kombi/kombi_person.vue';
import KombiPersonNummer from '@/components/kombi/kombi_person_nummer.vue';
import DateTextPickerField from '@/fields/DateTextPickerField.vue';
import WeiterePersonVersichern from '@/components/general/weitere_person_versichern.vue';
import PersonEntfernen from '@/components/general/person_entfernen.vue';
import OnlineAbschliessen from '@/components/general/online_abschliessen.vue';
import RdnummerDialog from '@/components/general/rdnummer_dialog.vue';
import RdnummerOk from '@/components/general/rdnummer_ok.vue'
import FehleranzeigeKlein from '@/components/general/fehleranzeige_klein.vue'
import KombiZX2Popover from '@/components/kombi/kombi_zx2_popover.vue';

const store = useRechnerStore();
const mainStore = useMainStore();
const antragStore = useAntragStore();
// Prüft ob RD-Nummer-Dialog (Button) angezeigt werden soll
const shouldShowRdnummer = computed(() => {
        // Dialog nur anzeigen wenn Feature enabled UND noch keine RD-Nummer eingegeben
        return mainStore.aokEnabled && store.aok === 0
    })

// Prüft ob RD-Nummerninfo angezeigt werden soll
const shouldShowRdnummerInfo = computed(() => {
        // Nur anzeigen wenn Feature enabled UND RD-Nummer vorhanden (nicht 0)
        return mainStore.aokEnabled && store.aok !== 0
    })

// FormValidation Instanz
let fv = null;

// API Call Status
const isGeneratingAntragId = ref(false);
const antragIdError = ref('');

// Geburtsdatum-Validierung
const maxDate = moment();
const minDate = moment().subtract(105, 'years');
const geburtstagsValidation = {
  notEmpty: {
    message: 'Bitte wählen Sie ein Geburtsdatum aus.',
  },
  date: {
    format: 'YYYY-MM-DD',
    max: maxDate.format('YYYY-MM-DD'),
    min: minDate.format('YYYY-MM-DD'),
    message: `Das Datum muss zwischen ${minDate.format('DD.MM.YYYY')} und ${maxDate.format('DD.MM.YYYY')} liegen.`,
  },
};

// Validierungsregeln pro Person generieren
function generateItemValidationRules(item) {
  return {
    [`vpGeburtstag${item.id}`]: {
      validators: geburtstagsValidation
    },
    [`vpTarif${item.id}`]: {
      validators: {
        callback: {
          message: 'Bitte wählen Sie mindestens einen Tarif aus.',
          callback: () => store.isTarifValid(item.id)
        }
      }
    }
  };
}

// Validierung für neue Person hinzufügen
function addItemValidation(id) {
  if (!fv) return;
  fv.addField(`vpGeburtstag${id}`, {
    validators: geburtstagsValidation
  });
  fv.addField(`vpTarif${id}`, {
    validators: {
      callback: {
        message: 'Bitte wählen Sie mindestens einen Tarif aus.',
        callback: () => store.isTarifValid(id)
      }
    }
  });
}

// Prüft ob Feld existiert und entfernt es nur dann (verhindert Fehler bei dynamischen Feldern)
function removeFieldIfExists(fieldName) {
  if (!fv) return;
  try {
    const field = fv.getFields()[fieldName];
    if (field) fv.removeField(fieldName);
  } catch {
    // Feld existiert nicht, ignorieren
  }
}

// Validierung für Person entfernen
function removeItemValidation(id) {
  if (!fv) return;
  removeFieldIfExists(`vpGeburtstag${id}`);
  removeFieldIfExists(`vpTarif${id}`);
}

// Aktualisiert das Geburtsdatum und triggert Neuberechnung
const updateGeburtstag = (itemId, value) => {
  const item = store.items.find(i => i.id === itemId);
  if (item) {
    item.geburtstag = value;
    store.setItem(itemId, item);
  }
};

// Tarif-Änderung behandeln (für Revalidierung)
const onTarifChanged = (itemId, tarifNummer) => {
  if (fv) {
    fv.revalidateField(`vpTarif${itemId}`);
  }
  // ZX2-Checkbox Validierung dynamisch hinzufügen/entfernen (nur wenn Checkbox sichtbar, d.h. nicht vigoselect)
  if (tarifNummer === 3) {
    nextTick(() => {
      if (store.hatZX2() && mainStore.kanal !== 'vigoselect') {
        fv?.addField('vpCheckZx2', {
          validators: {
            notEmpty: {
              message: 'Bitte bestätigen Sie die Angaben zu den fehlenden Zähnen.'
            }
          }
        });
      } else {
        removeFieldIfExists('vpCheckZx2');
      }
    });
  }
};

// Fügt eine weitere Person basierend auf der letzten hinzu (max 5 in Schritt 0)
const personHinzufuegen = () => {
  if (store.items.length >= 5) return;
  store.addItemBasedOnLast();
  nextTick(() => {
    addItemValidation(store.items[store.items.length - 1].id);
  });
};

// Person entfernen
const personEntfernen = (itemId) => {
  removeItemValidation(itemId);
  store.deleteItem(itemId);
};

// Submit-Handler
const handleWeiter = async (event) => {
  event.preventDefault();
  
  // Verhindere mehrfache Klicks während API Call läuft
  if (isGeneratingAntragId.value) {
    return;
  }
  
  if (fv) {
    const button = document.querySelector('[data-shake]');
    fv.validate().then(async (status) => {
      if (status === 'Valid') {
        // Formular ist valide → AntragId generieren
        antragIdError.value = '';
        isGeneratingAntragId.value = true;
        
        try {
          const success = await antragStore.generateAntragId();
          
          if (success && antragStore.hasValidAntragId) {
            // AntragId erfolgreich generiert → Weiter zu Schritt 1
            // MATOMO-DEAKTIVIERT (TODO: reaktivieren)
            //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Start', 'JetztOnlineAbschliessen']);
            store.stepStatus = 1;
          } else {
            // Fehler beim Generieren der AntragId
            antragIdError.value = antragStore.nachricht || 'Fehler beim Generieren der Antragsnummer. Bitte versuchen Sie es erneut.';
            console.error('[kombi_schritt0] Fehler bei AntragId-Generierung:', antragStore.returnCode, antragStore.nachricht);
            
            if (button) {
              button.classList.add('shake');
              setTimeout(() => button.classList.remove('shake'), 500);
            }
          }
        } catch (error) {
          // Exception abfangen
          antragIdError.value = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
          console.error('[kombi_schritt0] Exception bei AntragId-Generierung:', error);
          
          if (button) {
            button.classList.add('shake');
            setTimeout(() => button.classList.remove('shake'), 500);
          }
        } finally {
          isGeneratingAntragId.value = false;
        }
      } else {
        // Formular nicht valide
        if (button) {
          button.classList.add('shake');
          setTimeout(() => button.classList.remove('shake'), 500);
        }
        nextTick(() => {
          const firstInvalid = document.querySelector('#kombiAntrag .is-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    });
  }
};

const selectedVersicherungsbeginn = computed({
  get: () => store.versicherungsbeginn,
  set: (value) => {
    if (isValidDate(value)) {
      store.versicherungsbeginn = value;
      store.calculateAll();
    }
  }
});

const minStartDate = new Date(2025, 6, 1); // 01.07.2025 Javascript ist blöd
const today = new Date();
const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
const startMonth = today < minStartDate ? minStartDate : nextMonth;

const versbeginnoptions = computed(() => {
  const monthsToAdd = 6;
  let optionsArray = [];
  for (let i = 0; i < monthsToAdd; i++) {
    const optionDate = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);    
    optionsArray.push(optionDate.toLocaleDateString('de-DE', {year: 'numeric', month: '2-digit', day: '2-digit'}));
  }
  return optionsArray;
});

// Initialisierung
onMounted(() => {
  nextTick(() => {
    // Basis-Felder
    const fields = {
      versbeginn: {
        validators: {
          notEmpty: {
            message: 'Bitte wählen Sie einen Versicherungsbeginn aus.',
          },
        },
      },
    };
    
    // Felder für alle vorhandenen Personen
    store.items.forEach(item => {
      Object.assign(fields, generateItemValidationRules(item));
    });
    
    // FormValidation initialisieren
    fv = formValidation(document.getElementById('kombiAntrag'), {
      fields,
      plugins: {
        bootstrap5: new Bootstrap5(),
        submitButton: new SubmitButton(),
        trigger: new Trigger({
          event: 'blur change',
        }),
      },
    });
    
    // ZX2-Checkbox Validierung hinzufügen, falls bereits ZX2 ausgewählt und Checkbox sichtbar (nicht vigoselect)
    if (store.hatZX2() && mainStore.kanal !== 'vigoselect') {
      fv.addField('vpCheckZx2', {
        validators: {
          notEmpty: {
            message: 'Bitte bestätigen Sie die Angaben zu den fehlenden Zähnen.'
          }
        }
      });
    }
  });
});
</script>

<style scoped>
    .introclaim {
        margin-bottom: 1.5rem;
    }
    .platziert {
        position: absolute;
        right:0;
        top:0;
        transform: translate(-20%,-50%);
    }


    .schritt-zaehler-position {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    }

    @media (min-width: 768px) {
    .schritt-zaehler-position {
        width: 80vw;
    }
    }

    @media (min-width: 992px) {
    .schritt-zaehler-position {
        width: 70vw;
    }
    }

    @media (min-width: 1200px) {
    .schritt-zaehler-position {
        width: 60vw;
    }
    }

    h3 {
        color: #000;
        font-family: "Source Sans Pro";
        font-size: 22px;
        font-style: normal;
        font-weight: 700;
        line-height: 160%;
    }
</style>
